import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, Fingerprint } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { VentLogo } from '@/components/VentLogo';
import SEOHead from '@/components/SEOHead';
import { usePasswordValidation } from '@/hooks/usePasswordValidation';
import { setRememberMePreference, isNativeApp, getRememberedEmail, setRememberedEmail } from '@/lib/authStorage';

import { useBiometricLogin } from '@/hooks/useBiometricLogin';
import * as analytics from '@/lib/analytics';
import { normalizeEmail, validateEmailFormat, suggestEmailCorrection } from '@/lib/emailValidation';

const Auth = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [confirmEmailError, setConfirmEmailError] = useState<string | null>(null);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    validatePassword,
    getErrorMessages
  } = usePasswordValidation();
  const {
    hasSavedCredentials,
    loading: biometricLoading,
    saveCredentials: saveBiometricCredentials,
    biometricSignIn,
  } = useBiometricLogin();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const signUpStartedRef = useRef(false);
  const HCAPTCHA_SITE_KEY = '910a6856-1332-4550-9a6e-b27c42d83c04';

  // Prefill remembered email (we never store passwords)
  useEffect(() => {
    let mounted = true;
    getRememberedEmail().then(savedEmail => {
      if (!mounted) return;
      if (savedEmail) {
        setEmail(savedEmail);
        setRememberMe(true);
      }
    }).catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Finish a web OAuth round-trip: Supabase parses the session from the URL,
  // then we hand off to the app (ProtectedRoute sends new users to onboarding).
  useEffect(() => {
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem('oauth_in_progress');
    } catch {
      pending = null;
    }
    if (!pending || !user) return;
    try {
      sessionStorage.removeItem('oauth_in_progress');
    } catch {
      /* storage unavailable */
    }
    analytics.track('Login Completed', {
      auth_method: pending === 'apple' ? 'apple' : 'google',
    });
    navigate('/app', { replace: true });
  }, [user, navigate]);

  // Check for password reset flow and establish session from URL hash
  useEffect(() => {
    const type = searchParams.get('type');
    const recoveryPending = sessionStorage.getItem('password_recovery_pending');
    if (type === 'recovery' || recoveryPending === 'true') {
      sessionStorage.removeItem('password_recovery_pending');
      setIsPasswordReset(true);

      // Immediately try to recover session from URL hash tokens
      const recoverSession = async () => {
        const hash = window.location.hash;
        if (hash && hash.includes('access_token')) {
          const params = new URLSearchParams(hash.substring(1));
          const access_token = params.get('access_token') ?? '';
          const refresh_token = params.get('refresh_token') ?? '';
          if (access_token && refresh_token) {
            const {
              error
            } = await supabase.auth.setSession({
              access_token,
              refresh_token
            });
            if (!error) {
              // Clean the URL hash
              const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
              window.history.replaceState({}, document.title, cleanUrl);
            } else {
              toast({
                variant: 'destructive',
                title: 'Invalid reset link',
                description: 'This password reset link has expired or is invalid. Please request a new one.'
              });
              setIsPasswordReset(false);
            }
          }
        }
      };
      recoverSession();
    }
  }, [searchParams, toast]);

  // Check for deletion_pending flag in URL
  useEffect(() => {
    if (searchParams.get('deletion_pending') === 'true') {
      toast({
        variant: 'destructive',
        title: 'Account Pending Deletion',
        description: 'Your account has been marked for deletion and is no longer accessible.'
      });
    }
  }, [searchParams, toast]);

  // Allow access to auth page even when logged in (users can sign out to switch accounts)

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Set remember me preference BEFORE signing in
    // This ensures the storage adapter routes to the correct storage
    setRememberMePreference(rememberMe);
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken: captchaToken ?? undefined }
      });
      if (error) {
        // If the user hasn't confirmed their email yet, show the verification UI
        if (error.message.toLowerCase().includes('email') && error.message.toLowerCase().includes('confirm')) {
          localStorage.setItem('pendingVerificationEmail', email);
          setShowVerification(true);
          toast({
            title: 'Verify your email',
            description: 'Please verify your email address to finish signing in.'
          });
          return;
        }
        toast({
          variant: 'destructive',
          title: 'Error signing in',
          description: error.message
        });
        return;
      }
      if (data.user) {
        analytics.track('Login Completed', { auth_method: 'email' });
        // Remember email if opted-in (works on native + web)
        await setRememberedEmail(rememberMe ? email : null);

        // Save credentials for biometric login on next visit
        await saveBiometricCredentials(email, password);

        // Partner-only logins (school/employer dashboards) go straight to the partner dashboard.
        if ((data.user.app_metadata as Record<string, unknown> | undefined)?.account_type === 'partner') {
          toast({ title: 'Welcome back!', description: 'Opening your partner dashboard.' });
          navigate('/partner', { replace: true });
          return;
        }

        // Check if user is marked as provider-only in their profile
        const {
          data: profileData
        } = await supabase.from('profiles').select('user_id, is_therapist_only').eq('user_id', data.user.id).single();

        // If user is marked as provider-only, block access and direct to provider portal
        if (profileData?.is_therapist_only) {
          await supabase.auth.signOut();
          toast({
            variant: 'destructive',
            title: 'Provider Account',
            description: 'This is a provider account. Please use the Provider Portal to sign in.'
          });
          await setRememberedEmail(null);
          setLoading(false);
          return;
        }

        // Check if user has admin role
        const {
          data: roleData
        } = await supabase.rpc('has_role', {
          _user_id: data.user.id,
          _role: 'admin'
        });
        toast({
          title: 'Welcome back!',
          description: 'You have been signed in successfully.'
        });

        // Redirect admin users to dashboard selector
        if (roleData === true) {
          navigate('/dashboard-selector');
        } else {
          navigate('/app');
        }
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred.'
      });
    } finally {
      setLoading(false);
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
    }
  };
  const handleForgotPassword = async () => {
    if (!email) {
      toast({
        variant: 'destructive',
        title: 'Email required',
        description: 'Please enter your email address first.'
      });
      return;
    }
    setResetLoading(true);
    try {
      // Use production URL for email redirects to avoid localhost issues
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname.includes('preview') ? 'https://theventapp.com' : window.location.origin;
      const redirectUrl = `${baseUrl}/auth?type=recovery`;
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
        captchaToken: captchaToken ?? undefined,
      });
      if (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message
        });
      } else {
        toast({
          title: 'Check your email',
          description: 'We sent you a password reset link.'
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred.'
      });
    } finally {
      setResetLoading(false);
    }
  };
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast({
        variant: 'destructive',
        title: 'Passwords do not match',
        description: 'Please make sure both passwords match.'
      });
      return;
    }

    // Validate password strength
    const isValid = await validatePassword(newPassword);
    if (!isValid) {
      const errors = getErrorMessages();
      toast({
        variant: 'destructive',
        title: 'Password does not meet requirements',
        description: errors.join(', ')
      });
      return;
    }

    // Verify session exists before updating password
    const {
      data: sessionResult
    } = await supabase.auth.getSession();
    if (!sessionResult.session) {
      toast({
        variant: 'destructive',
        title: 'Session expired',
        description: 'Please click the reset link from your email again to continue.'
      });
      setIsPasswordReset(false);
      return;
    }
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) {
        toast({
          variant: 'destructive',
          title: 'Error updating password',
          description: error.message
        });
      } else {
        toast({
          title: 'Password updated!',
          description: 'Your password has been successfully updated.'
        });
        setIsPasswordReset(false);
        navigate('/app');
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred.'
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Email hygiene: normalize, validate format, and require a matching confirmation
    const cleanEmail = normalizeEmail(email);
    const cleanConfirmEmail = normalizeEmail(confirmEmail);
    if (cleanEmail !== email) setEmail(cleanEmail);
    if (cleanConfirmEmail !== confirmEmail) setConfirmEmail(cleanConfirmEmail);

    const emailCheck = validateEmailFormat(cleanEmail);
    if (!emailCheck.valid) {
      setEmailError(emailCheck.error ?? null);
      toast({
        variant: 'destructive',
        title: 'Check your email address',
        description: emailCheck.error ?? 'Please enter a valid email address.'
      });
      return;
    }
    setEmailError(null);

    if (cleanEmail !== cleanConfirmEmail) {
      setConfirmEmailError('Email addresses do not match');
      toast({
        variant: 'destructive',
        title: 'Emails do not match',
        description: 'Please make sure both email fields are the same.'
      });
      return;
    }
    setConfirmEmailError(null);

    const suggestion = suggestEmailCorrection(cleanEmail);
    setEmailSuggestion(suggestion);

    if (password !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are the same.'
      });
      return;
    }

    if (!acceptedTerms) {
      toast({
        variant: 'destructive',
        title: 'Terms and conditions required',
        description: 'Please accept the Terms of Service and Privacy Policy and confirm you are at least 16 years old to continue.'
      });
      return;
    }
    // Client-side password validation before attempting sign up
    const isValid = await validatePassword(password);
    if (!isValid) {
      toast({
        variant: 'destructive',
        title: 'Weak or compromised password',
        description: getErrorMessages().join(' • ')
      });
      return;
    }
    setLoading(true);
    try {
      // Sign up flow - Supabase handles enumeration prevention internally
      // Use production URL for email redirects to avoid localhost issues
      const baseUrl = window.location.hostname === 'localhost' || window.location.hostname.includes('preview') ? 'https://theventapp.com' : window.location.origin;
      const redirectUrl = `${baseUrl}/auth`;
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email: cleanEmail,

        password,
        options: {
          emailRedirectTo: redirectUrl,
          captchaToken: captchaToken ?? undefined,
        }
      });
      analytics.track('Email Submitted', { success: !error });
      if (error) {
        // Check if it's an existing user error
        if (error.message.includes('already registered')) {
          toast({
            variant: 'destructive',
            title: 'Account exists',
            description: 'This email is already registered. Please sign in instead or use the provider login if you have a provider account.'
          });
        } else {
          toast({
            variant: 'destructive',
            title: 'Error signing up',
            description: error.message
          });
        }
      } else if (data.user) {
        // Check if user already exists - Supabase returns identities as empty array for existing users
        // to prevent user enumeration, but we can detect this case
        const isExistingUser = data.user.identities && data.user.identities.length === 0;
        if (isExistingUser) {
          toast({
            variant: 'destructive',
            title: 'Account already exists',
            description: 'An account with this email already exists. Please sign in instead.'
          });
          return;
        }

        // Store email for the verification screen
        localStorage.setItem('pendingVerificationEmail', email);
        toast({
          title: 'Check your email',
          description: 'We sent you a verification code.'
        });

        // On native, navigate to the dedicated verify-email screen (OTP-first UX)
        if (isNativeApp()) {
          navigate('/verify-email');
        } else {
          setShowVerification(true);
        }
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred.'
      });
    } finally {
      setLoading(false);
      captchaRef.current?.resetCaptcha();
      setCaptchaToken(null);
    }
  };
  const handleVerifyOTP = async (code: string) => {
    if (!code || code.length !== 6) {
      toast({
        variant: 'destructive',
        title: 'Invalid code',
        description: 'Please enter a valid 6-digit verification code'
      });
      return;
    }
    setVerifying(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.verifyOtp({
        email: email,
        token: code,
        type: 'signup'
      });
      if (error) {
        toast({
          variant: 'destructive',
          title: 'Verification failed',
          description: error.message
        });
        return;
      }
      if (data.user) {
        toast({
          title: 'Email verified!',
          description: 'Your account has been verified successfully.'
        });
        setTimeout(() => {
          navigate("/onboarding");
        }, 1000);
      }
    } catch (error: any) {
      console.error("Verification error:", error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to verify code. Please try again.'
      });
    } finally {
      setVerifying(false);
    }
  };
  const handleChangeEmail = () => {
    setShowVerification(false);
    setVerificationCode('');
    setConfirmEmail('');
    setEmailError(null);
    setEmailSuggestion(suggestEmailCorrection(email));
    localStorage.removeItem('pendingVerificationEmail');
    toast({
      title: 'Update your email',
      description: 'Correct your email address and sign up again.'
    });
    setTimeout(() => document.getElementById('signup-email')?.focus(), 50);
  };
  const handleResendVerification = async () => {

    setLoading(true);
    try {
      // Prefer the typed email, but fall back to the last pending email and then the current user's email
      let targetEmail = email || localStorage.getItem('pendingVerificationEmail') || '';
      if (!targetEmail) {
        const {
          data: {
            user
          }
        } = await supabase.auth.getUser();
        targetEmail = user?.email ?? '';
      }
      if (!targetEmail) {
        toast({
          variant: 'destructive',
          title: 'Email required',
          description: 'Please enter your email first or sign in again.'
        });
        return;
      }

      // Call our custom edge function that sends via Resend directly
      const {
        data,
        error
      } = await supabase.functions.invoke('resend-verification-email', {
        body: {
          email: targetEmail
        }
      });
      if (error) {
        console.error('Resend verification error:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message || 'Failed to send verification email'
        });
      } else {
        localStorage.setItem('pendingVerificationEmail', targetEmail);
        toast({
          title: 'Email sent',
          description: 'Verification email sent! Please check your inbox.'
        });
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to resend verification email'
      });
    } finally {
      setLoading(false);
    }
  };
  const native = isNativeApp();

  return <div className={`min-h-[100dvh] flex flex-col items-center ${native ? 'justify-start pt-[max(3rem,env(safe-area-inset-top))]' : 'justify-center pt-[max(1rem,env(safe-area-inset-top))]'} p-4 pb-[max(1rem,env(safe-area-inset-bottom))] overflow-y-auto overscroll-none scrollbar-hide`} style={{
    background: 'linear-gradient(180deg, hsl(210, 20%, 58%) 0%, hsl(205, 25%, 72%) 45%, hsl(35, 35%, 88%) 100%)',
    backgroundColor: 'hsl(210, 20%, 58%)',
  }}>
      <SEOHead title="Sign In — Vent App" description="Sign in or create a free account on Vent, the anonymous mental health support platform." canonicalPath="/auth" />
      <div className="w-full max-w-md mb-4">
        <Button variant="ghost" onClick={() => navigate('/')} className="text-foreground hover:bg-background/20">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>
      </div>
      <Card className="w-full max-w-md bg-card text-card-foreground">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <VentLogo />
          </div>
          <CardTitle className="text-2xl">
            {isPasswordReset ? 'Reset Your Password' : 'Welcome to Vent'}
          </CardTitle>
          <CardDescription>
            {isPasswordReset ? 'Enter your new password below' : 'Your anonymous space to share and connect'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPasswordReset ? <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input id="newPassword" type="password" placeholder="Enter new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required autoComplete="new-password" name="new-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmNewPassword">Confirm New Password</Label>
                <Input id="confirmNewPassword" type="password" placeholder="Confirm new password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} required autoComplete="new-password" name="confirm-new-password" />
              </div>
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-xs font-medium text-foreground mb-2">Password must meet these requirements:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>At least 14 characters long</li>
                  <li>Include uppercase and lowercase letters</li>
                  <li>Include at least one number</li>
                  <li>Include at least one special character</li>
                  <li>Not appear in known data breaches</li>
                </ul>
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Updating...' : 'Update Password'}
              </Button>
            </form> : <>
            <div className="mb-4" />
            <Tabs defaultValue="signup" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="therapist">I'm a Provider</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Input id="email" type="email" placeholder="Enter your email" value={email} onChange={e => setEmail(e.target.value)} required className={hasSavedCredentials ? 'pr-12' : ''} autoComplete="username" name="email" />
                    {hasSavedCredentials && (
                      <button
                        type="button"
                        onClick={async () => {
                          const result = await biometricSignIn();
                          if (result.success) {
                            toast({
                              title: 'Welcome back!',
                              description: 'You have been signed in successfully.'
                            });
                            if ((result as { isPartner?: boolean }).isPartner) {
                              navigate('/partner');
                            } else if (result.isAdmin) {
                              navigate('/dashboard-selector');
                            } else {
                              navigate('/app');
                            }
                          } else if (result.isTherapistOnly) {
                            toast({
                              variant: 'destructive',
                              title: 'Provider Account',
                              description: 'This is a provider account. Please use the Provider Portal to sign in.'
                            });
                          }
                        }}
                        disabled={biometricLoading}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                        aria-label="Sign in with Face ID"
                      >
                        <Fingerprint className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" name="password" />
                </div>
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center space-x-3">
                    <Checkbox id="remember-me" checked={rememberMe} onCheckedChange={async checked => {
                    const next = checked as boolean;
                    setRememberMe(next);
                    if (!next) {
                      await setRememberedEmail(null);
                    }
                  }} className="h-5 w-5 border-2" />
                    <Label htmlFor="remember-me" className="text-sm font-medium cursor-pointer select-none">
                      Remember me
                    </Label>
                  </div>
                </div>
                {!isNativeApp() && (
                  <div className="flex justify-center">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={HCAPTCHA_SITE_KEY}
                      onVerify={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                    />
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                <Button type="button" variant="link" className="w-full" onClick={handleForgotPassword} disabled={resetLoading}>
                  {resetLoading ? 'Sending...' : 'Forgot Password?'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    inputMode="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      setEmailError(null);
                      setEmailSuggestion(null);
                    }}
                    onBlur={e => {
                      const normalized = normalizeEmail(e.target.value);
                      if (normalized !== e.target.value) setEmail(normalized);
                      if (!normalized) return;
                      const { valid, error } = validateEmailFormat(normalized);
                      setEmailError(valid ? null : error ?? null);
                      setEmailSuggestion(valid ? suggestEmailCorrection(normalized) : null);
                    }}
                    onFocus={() => {
                      if (signUpStartedRef.current) return;
                      signUpStartedRef.current = true;
                      analytics.track('Sign Up Started');
                    }}
                    required
                    autoComplete="username"
                    name="email"
                    aria-invalid={!!emailError}
                  />
                  {emailError && <p className="text-xs text-destructive">{emailError}</p>}
                  {!emailError && emailSuggestion && (
                    <p className="text-xs text-muted-foreground">
                      Did you mean{' '}
                      <button
                        type="button"
                        className="font-medium text-primary underline underline-offset-2"
                        onClick={() => {
                          setEmail(emailSuggestion);
                          setEmailSuggestion(null);
                          setEmailError(null);
                        }}
                      >
                        {emailSuggestion}
                      </button>
                      ?
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-email">Confirm Email</Label>
                  <Input
                    id="confirm-email"
                    type="email"
                    inputMode="email"
                    placeholder="Re-enter your email"
                    value={confirmEmail}
                    onChange={e => {
                      setConfirmEmail(e.target.value);
                      setConfirmEmailError(null);
                    }}
                    onBlur={e => {
                      const normalized = normalizeEmail(e.target.value);
                      if (normalized !== e.target.value) setConfirmEmail(normalized);
                      if (normalized && normalizeEmail(email) !== normalized) {
                        setConfirmEmailError('Email addresses do not match');
                      }
                    }}
                    required
                    autoComplete="off"
                    name="confirm-email"
                    aria-invalid={!!confirmEmailError}
                  />
                  {confirmEmailError && <p className="text-xs text-destructive">{confirmEmailError}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input id="signup-password" type="password" placeholder="Create a password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" name="new-password" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input id="confirm-password" type="password" placeholder="Confirm your password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required autoComplete="new-password" name="confirm-password" />
                </div>

                {password && !showVerification && <div className="bg-muted/30 border border-border rounded-lg p-3 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Password Requirements:</p>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={password.length >= 14 ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                          {password.length >= 14 ? "✓" : "○"}
                        </span>
                        <span className={password.length >= 14 ? "text-foreground" : "text-muted-foreground"}>
                          At least 14 characters
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={/[A-Z]/.test(password) ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                          {/[A-Z]/.test(password) ? "✓" : "○"}
                        </span>
                        <span className={/[A-Z]/.test(password) ? "text-foreground" : "text-muted-foreground"}>
                          One uppercase letter
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={/[a-z]/.test(password) ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                          {/[a-z]/.test(password) ? "✓" : "○"}
                        </span>
                        <span className={/[a-z]/.test(password) ? "text-foreground" : "text-muted-foreground"}>
                          One lowercase letter
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={/[0-9]/.test(password) ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                          {/[0-9]/.test(password) ? "✓" : "○"}
                        </span>
                        <span className={/[0-9]/.test(password) ? "text-foreground" : "text-muted-foreground"}>
                          One number
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?`~]/.test(password) ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                          {/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?`~]/.test(password) ? "✓" : "○"}
                        </span>
                        <span className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?`~]/.test(password) ? "text-foreground" : "text-muted-foreground"}>
                          One special character
                        </span>
                      </div>
                    </div>
                  </div>}

                {showVerification && <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-4">
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-foreground">Email Verification Required</h3>
                      <p className="text-xs text-muted-foreground">
                        We've sent a 6-digit verification code to <span className="font-medium text-foreground">{email}</span>. 
                        Please enter it below to verify your account.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="verification-code">Verification Code</Label>
                      <InputOTP maxLength={6} value={verificationCode} onChange={value => {
                    setVerificationCode(value);
                    if (value.length === 6) {
                      handleVerifyOTP(value);
                    }
                  }} disabled={verifying}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    <Button type="button" variant="outline" size="sm" onClick={handleResendVerification} disabled={loading} className="w-full">
                      {loading ? "Sending..." : `Resend email to ${email}`}
                    </Button>

                    <Button type="button" variant="link" size="sm" onClick={handleChangeEmail} className="w-full text-xs">
                      Wrong email? Change it
                    </Button>
                  </div>}

                
                 {!showVerification && <>
                    <div className="flex items-start space-x-2">
                      <Checkbox id="accept-terms" checked={acceptedTerms} onCheckedChange={checked => setAcceptedTerms(checked as boolean)} />
                      <Label htmlFor="accept-terms" className="text-sm leading-5">
                        I agree to the{' '}
                        <Link to="/terms" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                          Privacy Policy
                        </Link>
                        , and confirm I am at least 16 years old and legally permitted to use The Vent App under the laws of my country.
                         <span className="block mt-2 text-xs text-muted-foreground">
                           The Terms allow The Vent App to use de-identified excerpts from vents and other community posts in Vent-owned channels, advertising, press, and research. Your username and direct identifiers will not be shown, and private messages are excluded.
                         </span>
                      </Label>
                    </div>

                    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-2">
                      <p className="text-sm font-medium">📧 Email Verification Required</p>
                      <p className="text-xs text-muted-foreground">
                        After signing up, we'll send a verification email to your inbox. Click the link in the email to verify your account and begin the onboarding process.
                      </p>
                    </div>
                    
                    {!isNativeApp() && (
                      <div className="flex justify-center">
                        <HCaptcha
                          ref={captchaRef}
                          sitekey={HCAPTCHA_SITE_KEY}
                          onVerify={(token) => setCaptchaToken(token)}
                          onExpire={() => setCaptchaToken(null)}
                        />
                      </div>
                    )}
                    <Button type="submit" className="w-full" disabled={loading || !acceptedTerms}>
                      {loading ? 'Creating account...' : 'Sign Up'}
                    </Button>
                  </>}
              </form>
            </TabsContent>
            
            <TabsContent value="therapist">
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold">Join Our Mental Health Provider Directory</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect with clients seeking professional mental health support
                  </p>
                </div>
                
                <div className="bg-muted p-4 rounded-lg space-y-2">
                  <h4 className="font-medium">What's Included:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Verified professional profile</li>
                    <li>• Publish blog posts for users to read</li>
                    <li>• Discovery for your practice from users on the app</li>
                  </ul>
                </div>
                
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    &nbsp;&#8203;
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ​
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => navigate('/therapist-auth?tab=signin')} variant="outline" className="w-full">
                    Sign In
                  </Button>
                  <Button onClick={() => navigate('/therapist-auth?tab=signup')} className="w-full">
                    Sign Up
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs></>}
        </CardContent>
      </Card>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Need help?{' '}
        <a href="mailto:support@theventapp.com" className="underline hover:text-foreground">
          support@theventapp.com
        </a>
      </p>
    </div>;
};
export default Auth;